<template>
  <div class="ai-chat-widget-container">
    <AiChatWidgetChatBubble
      v-if="showChatBubble"
      :key="`bubble-${currentLanguage}-${languageChangeTrigger}`"
      :class="{ 'hidden-bubble': !aiChatEnabled || !displayChat }"
      :active-state="bubbleState"
      :client-info="clientInfo"
      @state-change="handleStateChange"
      @first-hover="handleFirstHover"
    />
    <AiChatWidgetSmartDrawer
      :key="`drawer-${currentLanguage}-${languageChangeTrigger}`"
      :is-open="isOpen"
      :active-state="bubbleState"
      :should-load="loadDrawer"
      :client-info="clientInfo"
      @submit="handleSubmitWithChatSessionId"
      @state-change="handleStateChange"
      @close="handleDrawerClose"
      @language-selected="handleLanguageSelected"
      @chatbot-click="handleChatbotClick"
      @message-sent="handleMessageSent"
      @message-received="handleMessageReceived"
      @chat-prompt-click="handleChatPromptClick"
      @force-end-session="handleForceEndSession"
      @organization-selected="handleOrganizationSelected"
      @analytics-event="handleAnalyticsEvent"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { getStreamConversation, deleteSession } from '~/helpers/api/stream';
import { BubbleState, setGlobalChatInstance, useAiChatWidget } from '~/composables/useAiChatWidget';
import { getStoredChatSession, setStoredChatSession, useSession } from '~/composables/useSession';
import { useAiChatClientInfo } from '~/composables/useAiChatClientInfo';
import {
  trackChatTranslationApplied,
  trackChatMessageSent,
  trackChatPromptClicked,
  trackChatConversationStarted,
  trackChatResponseViewed,
} from '~/helpers/chatWidgetAnalytics';

const emit = defineEmits(['chatbot-click']);

let chat = null;
let isConnecting = false;
let eventListenerAdded = false;
const sessionWasForceEnded = ref(false);

const store = useStore();
const chatConversationStarted = ref(false);
const secondaryOrgId = computed(() => String(store.state.secondaryOrgId ?? ''));
const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
const { sessionId: sId, resetSession } = useSession(secondaryOrgId);

const languageChangeTrigger = ref(0);

const { clientInfo, fetchTopics, contactFormTopics, buttonsConfiguration } = useAiChatClientInfo();
const {
  bubbleState,
  aiChatEnabled,
  displayChat,
  isOpen,
  loadDrawer,
  handleStateChange,
  handleClose,
  handleFirstHover,
  handleSubmit,
} = useAiChatWidget();
const { $intl } = useNuxtApp();
const currentLanguage = computed(() => $intl.locale);
const showChatBubble = computed(() => buttonsConfiguration.value.enabled.length >= 1);
const performSessionCleanup = (orgId, sessionIdToDelete, wasConnected) => {
  if (wasConnected && chat) {
    chat.disconnect();
  }
  if (wasConnected && sessionIdToDelete && orgId) {
    void deleteSession({
      endpoint: writableApi,
      secondaryOrgId: orgId,
      sessionId: sessionIdToDelete,
    });
  }
  if (orgId) {
    resetSession(orgId);
  }
};

/**
 * On page hide we only disconnect the live connection to release resources.
 * We do not delete the server session or clear localStorage, so the session
 * persists across tab switch, window switch, and refresh. Cleanup (delete + reset)
 * only runs when the user explicitly ends the session or after 2h expiry.
 */
const handlePageUnload = () => {
  if (chat?.isConnected.value) {
    chat.disconnect();
  }
};

const handleLanguageSelected = async (code) => {
  const language = clientInfo.value.languages.find((language) => language.code === code);
  if (language?.code !== $intl.locale) {
    $intl.$setLocale(language?.code);
    trackChatTranslationApplied(language?.code);
    languageChangeTrigger.value++;
  }
};

const handleChatbotClick = () => {
  emit('chatbot-click');
};
const handleMessageSent = (message) => {
  if (!chatConversationStarted.value) {
    trackChatConversationStarted(message);
    chatConversationStarted.value = true;
  }
  trackChatMessageSent(message);
};
const handleMessageReceived = (message) => {
  trackChatResponseViewed(message);
};
const handleChatPromptClick = (prompt) => {
  trackChatPromptClicked(prompt);
};

const handleOrganizationSelected = (orgId) => {
  fetchTopics(orgId);
};

const handleForceEndSession = () => {
  sessionWasForceEnded.value = true;
  handleDrawerClose();
  const orgId = secondaryOrgId.value;
  const sessionIdToDelete = sId.value;
  // Disconnect and reset chat instance for a fresh session on next open
  performSessionCleanup(orgId, sessionIdToDelete, true);
  chat = null;
  setGlobalChatInstance(null);
};

const canInitializeStreamConnection = computed(() => {
  return import.meta.client && aiChatEnabled.value && !isConnecting;
});

const connectToStream = async () => {
  if (!canInitializeStreamConnection.value || !secondaryOrgId.value) return;

  if (!chat) {
    chat = useChat({
      onSessionExpired: () => {
        const orgId = secondaryOrgId.value;
        const sessionIdToDelete = sId.value;
        performSessionCleanup(orgId, sessionIdToDelete, true);
      },
    });
    setGlobalChatInstance(chat);
  }

  if (!chat.isConnected.value) {
    isConnecting = true;
    try {
      const config = {
        endpoint: writableApi,
        secondaryOrgId: secondaryOrgId.value,
        sessionId: sId.value,
      };
      const stored = getStoredChatSession(secondaryOrgId.value);
      const welcomeMessage = clientInfo.value.chatConfiguration?.branding?.welcome_message;
      const hasStreamSession = stored?.conversationId && stored?.userToken;

      const tokenProvider = async () => {
        const result = await getStreamConversation({
          ...config,
          existingSessionId: hasStreamSession ? stored?.sessionId : undefined,
        });
        setStoredChatSession(secondaryOrgId.value, {
          sessionId: result.user,
          conversationId: result.channelId,
          userToken: result.token,
        });
        return result;
      };

      await chat.connectWithProvider(tokenProvider, config, {
        welcomeMessage: welcomeMessage,
      });
    } catch (e) {
      console.error("Can't connect to Stream", e);
    } finally {
      isConnecting = false;
    }
  }
};

const chatWidgetReady = computed(() => {
  return import.meta.client || aiChatEnabled.value;
});

const restoreAndOpenChat = async () => {
  loadDrawer.value = true;
  await connectToStream();
  bubbleState.value = BubbleState.AiChat;
};

const handleClosedState = async (orgId) => {
  if (chat?.isConnected.value) return;
  const stored = getStoredChatSession(orgId);
  if (stored?.conversationId && stored?.userToken) {
    void restoreAndOpenChat();
  }
};

const removePageUnloadListener = () => {
  if (eventListenerAdded) {
    window.removeEventListener('pagehide', handlePageUnload);
    eventListenerAdded = false;
  }
};

const addPageUnloadListener = () => {
  if (!eventListenerAdded) {
    window.addEventListener('pagehide', handlePageUnload);
    eventListenerAdded = true;
  }
};

watch(
  [bubbleState, secondaryOrgId, chatWidgetReady],
  async ([newState, orgId, ready]) => {
    if (!import.meta.client || !ready || !orgId) return;

    if (newState === BubbleState.Closed) {
      if (sessionWasForceEnded.value) {
        removePageUnloadListener();
      } else {
        await handleClosedState(orgId);
        if (!chat?.isConnected.value) {
          removePageUnloadListener();
        }
      }
      return;
    }

    addPageUnloadListener();

    if (newState === BubbleState.AiChat) {
      connectToStream();
    }

    if (newState === BubbleState.Inbox) {
      fetchTopics(orgId);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  window.removeEventListener('pagehide', handlePageUnload);
  chatConversationStarted.value = false;
});

const handleSubmitWithChatSessionId = (newFormData) => {
  const formData = { ...(newFormData ?? {}), chatSessionId: chat?.channelId?.value };
  if (!formData.subject?.trim() && formData.topic != null) {
    const topicLabel =
      contactFormTopics.value.find((t) => Number(t.id) === Number(formData.topic))?.name ?? 'General Inquiry';
    formData.subject = `Contact Form Submission - ${topicLabel}`;
  }
  handleSubmit(formData);
};

const handleDrawerClose = (newFormData) => {
  handleClose(newFormData ?? {});
};

const handleAnalyticsEvent = (eventName, payload) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, payload);
  }
};
</script>

<style scoped>
:deep(.hidden-bubble) {
  .bubble-message-container {
    display: none;
  }
}
</style>
